import React, { useState, useId } from 'react';
import { Upload, X, Plus } from 'lucide-react';

/**
 * Uploader pour une galerie d'images. Convertit chaque fichier en Base64.
 * Props:
 *  - value: string[] (tableau de data URIs ou URLs existantes)
 *  - onChange: (array) => void
 *  - label: string
 *  - maxSizeMB: number (par image, défaut 3)
 *  - maxImages: number (défaut 12)
 */
const GalleryUploader = ({ value = [], onChange, label = 'Galerie', maxSizeMB = 3, maxImages = 12 }) => {
  const inputId = useId();
  const [error, setError] = useState('');

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setError('');

    if (value.length + files.length > maxImages) {
      setError(`Vous ne pouvez ajouter que ${maxImages} images maximum.`);
      return;
    }

    const promises = files.map((file) => {
      return new Promise((resolve, reject) => {
        if (!file.type.startsWith('image/')) {
          reject(new Error('Type non supporté : ' + file.name));
          return;
        }
        if (file.size > maxSizeMB * 1024 * 1024) {
          reject(new Error(`${file.name} dépasse ${maxSizeMB} MB.`));
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Erreur de lecture'));
        reader.readAsDataURL(file);
      });
    });

    Promise.all(promises)
      .then((dataUrls) => {
        onChange([...value, ...dataUrls]);
      })
      .catch((err) => setError(err.message));

    e.target.value = '';
  };

  const removeAt = (index) => {
    const next = [...value];
    next.splice(index, 1);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {label && (
        <label className="block text-sm font-semibold">{label}</label>
      )}

      {value.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {value.map((src, idx) => (
            <div key={idx} className="relative group">
              <img src={src} alt={`Image ${idx + 1}`} className="w-full h-32 object-cover rounded-lg border border-gray-200" />
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="absolute top-1 right-1 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow opacity-90"
                aria-label="Supprimer cette image"
                data-testid={`gallery-remove-${idx}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {value.length < maxImages && (
        <div>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFiles}
            className="hidden"
            id={`gallery-${inputId}`}
          />
          <label
            htmlFor={`gallery-${inputId}`}
            className="inline-flex items-center cursor-pointer px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 hover:border-amber-400 text-gray-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            {value.length === 0 ? "Ajouter des images" : "Ajouter d'autres images"}
          </label>
          <p className="text-xs text-gray-500 mt-1">
            Sélectionnez une ou plusieurs images depuis votre ordinateur. Max {maxImages} images, {maxSizeMB} MB chacune.
          </p>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default GalleryUploader;
